import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  useTheme,
  alpha,
  Tabs,
  Tab,
  Avatar,
  Chip,
  Grid,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  ArrowForward as ArrowForwardIcon,
  AccessTime as RecentIcon,
  Star as FavoriteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  History as HistoryIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useManualBooking } from '../../../context/ManualBookingContext';
import GradientButton from '../../../../components/buttons/GradientButton';
import { listClients } from '../../../../api/companyRecords';

const CustomerStep = ({ onNext, onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [mode, setMode] = useState('browse'); // 'browse' or 'new'
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const theme = useTheme();
  const { bookingData, updateBookingData } = useManualBooking();

  // Fetch recent customers on component mount
  useEffect(() => {
    const fetchRecentCustomers = async () => {
      try {
        const clients = await listClients({
          companyId: bookingData.companyData.id,
          archived: false,
        });
        const recentData = [...clients]
          .sort((a, b) => {
            const aTime = Date.parse(String(a.lastBooking || a.updatedAt || '')) || 0;
            const bTime = Date.parse(String(b.lastBooking || b.updatedAt || '')) || 0;
            return bTime - aTime;
          })
          .slice(0, 6)
          .map((data) => ({
            id: data.id,
            name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.name,
            email: data.email,
            phone: data.phone,
            lastBooking: data.lastBooking,
            totalBookings: data.totalBookings || 0,
            ...data
          }));
        
        setRecentCustomers(recentData);
      } catch (err) {
        console.error('Error fetching recent customers:', err);
      }
    };

    fetchRecentCustomers();
  }, [bookingData.companyData.id]);

  // Search customers effect
  useEffect(() => {
    if (searchTerm.length < 2) {
      setCustomers([]);
      return;
    }

    const searchCustomers = async () => {
      setLoading(true);
      try {
        const searchTermLower = searchTerm.toLowerCase();
        const clients = await listClients({
          companyId: bookingData.companyData.id,
          archived: false,
        });

        const results = new Map();
        clients.slice(0, 40).forEach((data) => {
          if (
            data.searchName?.includes(searchTermLower) ||
            data.email?.toLowerCase().includes(searchTermLower) ||
            String(data.phone || '').includes(searchTerm) ||
            String(data.name || '').toLowerCase().includes(searchTermLower)
          ) {
            results.set(data.id, {
              id: data.id,
              name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.name,
              email: data.email,
              phone: data.phone,
              lastBooking: data.lastBooking,
              totalBookings: data.totalBookings || 0,
              ...data
            });
          }
        });

        setCustomers(Array.from(results.values()));
        setError(null);
      } catch (err) {
        console.error('Error searching customers:', err);
        setError('Failed to search customers. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    const debounceTimeout = setTimeout(searchCustomers, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, bookingData.companyData.id]);

  const handleSelectCustomer = (customer) => {
    // If clicking the same customer that's already selected, unselect them
    if (bookingData.customer?.id === customer.id) {
      updateBookingData({ customer: null });
    } else {
      updateBookingData({ customer });
    }
  };

  const handleNewCustomerChange = (field) => (event) => {
    const value = event.target.value;

    // Store raw digits for phone, but format visually in the input
    if (field === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setNewCustomer(prev => ({
        ...prev,
        phone: digitsOnly
      }));
      return;
    }

    setNewCustomer(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatPhoneNumber = (value) => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 10);

    if (digits.length <= 3) return digits;
    if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const validateNewCustomer = () => {
    const errors = {};
    if (!newCustomer.name.trim()) errors.name = 'Name is required';
    if (!newCustomer.email.trim()) errors.email = 'Email is required';
    if (!newCustomer.phone.trim()) errors.phone = 'Phone is required';
    if (newCustomer.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newCustomer.email)) {
      errors.email = 'Invalid email format';
    }
    if (newCustomer.phone) {
      const phoneDigits = newCustomer.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        errors.phone = 'Invalid phone format';
      }
    }
    return errors;
  };

  const handleSubmitNewCustomer = () => {
    const errors = validateNewCustomer();
    if (Object.keys(errors).length > 0) {
      setError('Please fill in all required fields correctly.');
      return;
    }

    updateBookingData({
      customer: {
        ...newCustomer,
        isNew: true,
      }
    });
    onNext();
  };

  const CustomerCard = ({ customer, isSelected }) => (
    <Box
      sx={{
        p: 2,
        cursor: 'pointer',
        borderRadius: 1,
        transition: 'all 0.2s ease',
        backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          transform: 'translateY(-2px)',
        },
      }}
      onClick={() => handleSelectCustomer(customer)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: isSelected 
              ? 'primary.main' 
              : customer.totalBookings > 0 
                ? alpha(theme.palette.success.main, 0.9)
                : theme.palette.grey[200],
            fontSize: '1rem',
            color: isSelected || customer.totalBookings > 0 ? 'white' : 'text.primary',
          }}
        >
          {customer.name.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 600,
              color: isSelected ? 'primary.main' : 'text.primary',
              mb: 0.5
            }}
            noWrap
          >
            {customer.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {customer.email && (
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  color: isSelected ? 'primary.main' : 'info.main',
                  fontWeight: 500,
                }}
                noWrap
              >
                <EmailIcon 
                  sx={{ 
                    fontSize: '0.9rem',
                    color: isSelected ? 'primary.main' : 'info.main',
                    opacity: 0.9
                  }} 
                />
                {customer.email}
              </Typography>
            )}
            {customer.phone && (
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  color: isSelected ? 'primary.main' : 'success.main',
                  fontWeight: 500,
                }}
                noWrap
              >
                <PhoneIcon 
                  sx={{ 
                    fontSize: '0.9rem',
                    color: isSelected ? 'primary.main' : 'success.main',
                    opacity: 0.9
                  }} 
                />
                {formatPhoneNumber(customer.phone)}
              </Typography>
            )}
          </Box>
        </Box>
        {customer.totalBookings > 0 && (
          <Chip
            size="small"
            label={`${customer.totalBookings}`}
            sx={{
              height: 24,
              minWidth: 24,
              borderRadius: '12px',
              bgcolor: isSelected 
                ? alpha(theme.palette.primary.main, 0.1)
                : alpha(theme.palette.success.main, 0.1),
              color: isSelected ? 'primary.main' : 'success.main',
              fontWeight: 600,
              '& .MuiChip-label': {
                px: 1,
                fontSize: '0.75rem',
              }
            }}
          />
        )}
      </Box>
    </Box>
  );

  const renderBrowseMode = () => (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              minWidth: 'auto',
              px: 3,
            }
          }}
        >
          <Tab 
            icon={<SearchIcon sx={{ fontSize: '1.2rem' }} />} 
            iconPosition="start" 
            label="Search" 
          />
          <Tab 
            icon={<RecentIcon sx={{ fontSize: '1.2rem' }} />} 
            iconPosition="start" 
            label="Recent" 
          />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Box>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search customers by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
          ) : customers.length > 0 ? (
            <Grid container spacing={2}>
              {customers.map((customer) => (
                <Grid item xs={12} sm={6} md={4} key={customer.id}>
                  <CustomerCard 
                    customer={customer} 
                    isSelected={bookingData.customer?.id === customer.id} 
                  />
                </Grid>
              ))}
            </Grid>
          ) : searchTerm.length >= 2 ? (
            <Box 
              sx={{ 
                textAlign: 'center', 
                p: 4, 
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 2
              }}
            >
              <Typography color="text.secondary">
                No customers found
              </Typography>
            </Box>
          ) : null}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Recently Booked Customers
          </Typography>
          <Grid container spacing={2}>
            {recentCustomers.map((customer) => (
              <Grid item xs={12} sm={6} md={4} key={customer.id}>
                <CustomerCard 
                  customer={customer} 
                  isSelected={bookingData.customer?.id === customer.id} 
                />
              </Grid>
            ))}
            {recentCustomers.length === 0 && (
              <Grid item xs={12}>
                <Box 
                  sx={{ 
                    textAlign: 'center', 
                    p: 4, 
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 2
                  }}
                >
                  <Typography color="text.secondary">
                    No recent customers
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <GradientButton
          startIcon={<PersonAddIcon />}
          onClick={() => setMode('new')}
          variant="primary"
          sx={{
            borderStyle: 'dashed',
            '&:hover': {
              borderStyle: 'solid',
            }
          }}
        >
          Add New Customer
        </GradientButton>
      </Box>
    </Box>
  );

  const renderNewCustomerForm = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        New Customer Information
      </Typography>

      <Box component="form" sx={{ mt: 2 }}>
        <TextField
          fullWidth
          label="Full Name"
          value={newCustomer.name}
          onChange={handleNewCustomerChange('name')}
          margin="normal"
          required
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={newCustomer.email}
          onChange={handleNewCustomerChange('email')}
          margin="normal"
          required
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Phone Number"
          value={formatPhoneNumber(newCustomer.phone)}
          onChange={handleNewCustomerChange('phone')}
          margin="normal"
          required
          sx={{ mb: 2 }}
        />
      </Box>

      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box>
      <Box>
        {mode === 'browse' ? renderBrowseMode() : renderNewCustomerForm()}
      </Box>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        <GradientButton 
          onClick={mode === 'new' ? () => setMode('browse') : onBack}
          variant="primary"
        >
          {mode === 'new' ? 'Back to Browse' : 'Back'}
        </GradientButton>
        <GradientButton
          onClick={mode === 'new' ? handleSubmitNewCustomer : onNext}
          disabled={mode === 'browse' ? !bookingData.customer : false}
          variant="success"
        >
          Continue
        </GradientButton>
      </Box>
    </Box>
  );
};

export default CustomerStep; 